import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { 
  Receipt,
  Plus,
  Pencil,
  Trash,
  X,
  Check,
  ArrowsDownUp,
  MagnifyingGlass,
  ListBullets,
  SquaresFour,
  CaretDown,
  CaretRight,
  CalendarBlank,
  Funnel
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState({
    vendor_name: "",
    invoice_date: new Date(),
    amount: "",
    refund_amount: "",
    notes: "",
    location_id: ""
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [viewMode, setViewMode] = useState("list"); // "list" or "vendor"
  const [expandedVendors, setExpandedVendors] = useState({});
  
  // Date range filter
  const [dateRange, setDateRange] = useState("all"); // "all", "this_month", "last_month", "custom"
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  // Vendors
  const [vendors, setVendors] = useState([]);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState({ name: "", contact_person: "", phone: "", email: "", notes: "" });

  // Group invoices by vendor
  const groupedByVendor = useMemo(() => {
    const filtered = invoices.filter(inv => {
      if (!invoiceSearch) return true;
      const q = invoiceSearch.toLowerCase();
      return inv.vendor_name?.toLowerCase().includes(q) || inv.notes?.toLowerCase().includes(q);
    });

    const groups = {};
    filtered.forEach(inv => {
      const vendor = inv.vendor_name || "Unknown Vendor";
      if (!groups[vendor]) {
        groups[vendor] = {
          invoices: [],
          totalAmount: 0,
          totalRefund: 0,
          totalNet: 0
        };
      }
      groups[vendor].invoices.push(inv);
      groups[vendor].totalAmount += inv.amount || 0;
      groups[vendor].totalRefund += inv.refund_amount || 0;
      groups[vendor].totalNet += (inv.amount || 0) - (inv.refund_amount || 0);
    });

    // Sort vendors by total net (descending)
    return Object.entries(groups)
      .sort(([, a], [, b]) => b.totalNet - a.totalNet)
      .map(([vendor, data]) => ({
        vendor,
        ...data,
        invoices: data.invoices.sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date))
      }));
  }, [invoices, invoiceSearch]);

  // Calculate grand totals
  const grandTotals = useMemo(() => {
    return groupedByVendor.reduce((acc, group) => ({
      amount: acc.amount + group.totalAmount,
      refund: acc.refund + group.totalRefund,
      net: acc.net + group.totalNet
    }), { amount: 0, refund: 0, net: 0 });
  }, [groupedByVendor]);

  // Filtered invoices for list view
  const filteredInvoicesList = useMemo(() => {
    if (!invoiceSearch) return invoices;
    const q = invoiceSearch.toLowerCase();
    return invoices.filter(inv => 
      inv.vendor_name?.toLowerCase().includes(q) || inv.notes?.toLowerCase().includes(q)
    );
  }, [invoices, invoiceSearch]);

  const toggleVendor = (vendor) => {
    setExpandedVendors(prev => ({
      ...prev,
      [vendor]: !prev[vendor]
    }));
  };

  const expandAll = () => {
    const all = {};
    groupedByVendor.forEach(g => { all[g.vendor] = true; });
    setExpandedVendors(all);
  };

  const collapseAll = () => {
    setExpandedVendors({});
  };

  useEffect(() => {
    fetchLocations();
    fetchInvoices();
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, startDate, endDate]);

  // Handle date range preset changes
  useEffect(() => {
    const today = new Date();
    switch (dateRange) {
      case "this_month":
        setStartDate(startOfMonth(today));
        setEndDate(endOfMonth(today));
        break;
      case "last_month":
        const lastMonth = subMonths(today, 1);
        setStartDate(startOfMonth(lastMonth));
        setEndDate(endOfMonth(lastMonth));
        break;
      case "all":
        setStartDate(null);
        setEndDate(null);
        break;
      // "custom" - don't change dates, user will pick them
      default:
        break;
    }
  }, [dateRange]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, location_id: response.data[0].location_id }));
      }
    } catch (error) {
      toast.error("Failed to load locations");
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API}/vendors`, { withCredentials: true });
      setVendors(res.data);
    } catch {
      console.error("Failed to load vendors");
    }
  };

  const handleSaveVendor = async () => {
    if (!vendorForm.name.trim()) { toast.error("Vendor name required"); return; }
    try {
      if (editingVendor?.vendor_id) {
        await axios.put(`${API}/vendors/${editingVendor.vendor_id}`, vendorForm, { withCredentials: true });
        toast.success("Vendor updated");
      } else {
        await axios.post(`${API}/vendors`, vendorForm, { withCredentials: true });
        toast.success("Vendor created");
      }
      setEditingVendor(null);
      setVendorForm({ name: "", contact_person: "", phone: "", email: "", notes: "" });
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vendor");
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await axios.delete(`${API}/vendors/${vendorId}`, { withCredentials: true });
      toast.success("Vendor deleted");
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const fetchInvoices = async () => {
    try {
      let url = `${API}/invoices`;
      const params = new URLSearchParams();
      
      if (selectedLocation !== "all") {
        params.append("location_id", selectedLocation);
      }
      if (startDate) {
        params.append("start_date", format(startDate, "yyyy-MM-dd"));
      }
      if (endDate) {
        params.append("end_date", format(endDate, "yyyy-MM-dd"));
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.vendor_name || !formData.amount || !formData.location_id) {
      toast.error("Please fill in vendor name, amount, and location");
      return;
    }

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        refund_amount: parseFloat(formData.refund_amount) || 0,
        invoice_date: formData.invoice_date.toISOString()
      };

      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.invoice_id}`, payload, { withCredentials: true });
        toast.success("Invoice updated");
      } else {
        await axios.post(`${API}/invoices`, payload, { withCredentials: true });
        toast.success("Invoice created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to save invoice");
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      vendor_name: invoice.vendor_name,
      invoice_date: new Date(invoice.invoice_date),
      amount: invoice.amount.toString(),
      refund_amount: (invoice.refund_amount || 0).toString(),
      notes: invoice.notes || "",
      location_id: invoice.location_id
    });
    setDialogOpen(true);
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm("Delete this invoice?")) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, { withCredentials: true });
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const resetForm = () => {
    setEditingInvoice(null);
    setFormData({
      vendor_name: "",
      invoice_date: new Date(),
      amount: "",
      refund_amount: "",
      notes: "",
      location_id: locations[0]?.location_id || ""
    });
  };

  const getLocationName = (locationId) => {
    const loc = locations.find(l => l.location_id === locationId);
    return loc?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
    <div data-testid="invoices-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
            VENDOR INVOICES
          </h1>
          <p className="text-muted-foreground mt-1">
            Track deliveries and refunds
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { setVendorDialogOpen(true); setEditingVendor(null); setVendorForm({ name: "", contact_person: "", phone: "", email: "", notes: "" }); }}
            data-testid="manage-vendors-btn"
          >
            Vendors
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { 
            setDialogOpen(open); 
            if (!open) resetForm(); 
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="add-invoice-btn">
                <Plus size={18} className="mr-2" />
                Add Invoice
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-wide">
                {editingInvoice ? "EDIT INVOICE" : "NEW INVOICE"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Location</Label>
                <Select 
                  value={formData.location_id} 
                  onValueChange={(val) => setFormData(p => ({ ...p, location_id: val }))}
                >
                  <SelectTrigger data-testid="invoice-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor</Label>
                <Select 
                  value={formData.vendor_name} 
                  onValueChange={(val) => setFormData(p => ({ ...p, vendor_name: val }))}
                >
                  <SelectTrigger data-testid="vendor-name-input">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.vendor_id} value={v.name}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Invoice Date</Label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="invoice-date-picker"
                    >
                      <CalendarBlank size={16} className="mr-2" />
                      {format(formData.invoice_date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.invoice_date}
                      onSelect={(date) => {
                        if (date) {
                          setFormData(p => ({ ...p, invoice_date: date }));
                          setDatePickerOpen(false);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount ($)</Label>
                  <Input 
                    data-testid="invoice-amount-input"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <Label>Refund ($)</Label>
                  <Input 
                    data-testid="invoice-refund-input"
                    type="number"
                    value={formData.refund_amount}
                    onChange={(e) => setFormData(p => ({ ...p, refund_amount: e.target.value }))}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input 
                  data-testid="invoice-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g., Delivery #1234"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="bg-primary" data-testid="save-invoice-btn">
                <Check size={16} className="mr-2" />
                {editingInvoice ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        {/* Main filters row */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="invoice-search"
              value={invoiceSearch}
              onChange={(e) => setInvoiceSearch(e.target.value)}
              placeholder="Search by vendor name or notes..."
              className="pl-10 pr-10"
            />
            {invoiceSearch && (
              <button onClick={() => setInvoiceSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X size={18} />
              </button>
            )}
          </div>
          <div className="w-48">
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="filter-location-select">
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* View Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              className={`rounded-none ${viewMode === "list" ? "" : "hover:bg-muted"}`}
              onClick={() => setViewMode("list")}
              data-testid="view-list-btn"
            >
              <ListBullets size={18} className="mr-1" />
              List
            </Button>
            <Button
              variant={viewMode === "vendor" ? "default" : "ghost"}
              size="sm"
              className={`rounded-none ${viewMode === "vendor" ? "" : "hover:bg-muted"}`}
              onClick={() => setViewMode("vendor")}
              data-testid="view-vendor-btn"
            >
              <SquaresFour size={18} className="mr-1" />
              By Vendor
            </Button>
          </div>
        </div>

        {/* Date range filters */}
        <div className="flex flex-wrap gap-3 items-center p-3 bg-muted/30 rounded-lg">
          <Funnel size={18} className="text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Date Range:</span>
          
          {/* Date preset buttons */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={dateRange === "all" ? "default" : "ghost"}
              size="sm"
              className={`rounded-none text-xs ${dateRange === "all" ? "" : "hover:bg-muted"}`}
              onClick={() => setDateRange("all")}
              data-testid="date-range-all"
            >
              All Time
            </Button>
            <Button
              variant={dateRange === "this_month" ? "default" : "ghost"}
              size="sm"
              className={`rounded-none text-xs ${dateRange === "this_month" ? "" : "hover:bg-muted"}`}
              onClick={() => setDateRange("this_month")}
              data-testid="date-range-this-month"
            >
              This Month
            </Button>
            <Button
              variant={dateRange === "last_month" ? "default" : "ghost"}
              size="sm"
              className={`rounded-none text-xs ${dateRange === "last_month" ? "" : "hover:bg-muted"}`}
              onClick={() => setDateRange("last_month")}
              data-testid="date-range-last-month"
            >
              Last Month
            </Button>
            <Button
              variant={dateRange === "custom" ? "default" : "ghost"}
              size="sm"
              className={`rounded-none text-xs ${dateRange === "custom" ? "" : "hover:bg-muted"}`}
              onClick={() => setDateRange("custom")}
              data-testid="date-range-custom"
            >
              Custom
            </Button>
          </div>

          {/* Custom date pickers */}
          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <Popover open={startDatePickerOpen} onOpenChange={setStartDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    data-testid="start-date-picker"
                  >
                    <CalendarBlank size={14} className="mr-1" />
                    {startDate ? format(startDate, "MMM d, yyyy") : "Start Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartDatePickerOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              <span className="text-muted-foreground">to</span>
              
              <Popover open={endDatePickerOpen} onOpenChange={setEndDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    data-testid="end-date-picker"
                  >
                    <CalendarBlank size={14} className="mr-1" />
                    {endDate ? format(endDate, "MMM d, yyyy") : "End Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setEndDatePickerOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {(startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-muted-foreground"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                  }}
                >
                  <X size={14} />
                </Button>
              )}
            </div>
          )}

          {/* Show active date range */}
          {dateRange !== "all" && dateRange !== "custom" && startDate && endDate && (
            <span className="text-xs text-muted-foreground">
              {format(startDate, "MMM d")} - {format(endDate, "MMM d, yyyy")}
            </span>
          )}
        </div>
      </div>

      {/* Invoices Display */}
      {viewMode === "list" ? (
        /* List View */
        <Card className="border rounded-xl">
          <CardContent className="p-0">
            {invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-heading tracking-wide">DATE</TableHead>
                      <TableHead className="font-heading tracking-wide">VENDOR</TableHead>
                      <TableHead className="font-heading tracking-wide">LOCATION</TableHead>
                      <TableHead className="font-heading tracking-wide text-right">AMOUNT</TableHead>
                      <TableHead className="font-heading tracking-wide text-right">REFUND</TableHead>
                      <TableHead className="font-heading tracking-wide text-right">NET</TableHead>
                      <TableHead className="font-heading tracking-wide">NOTES</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoicesList.map(invoice => (
                      <TableRow key={invoice.invoice_id} data-testid={`invoice-row-${invoice.invoice_id}`}>
                        <TableCell className="tabular-nums">
                          {format(new Date(invoice.invoice_date), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell className="font-medium">{invoice.vendor_name}</TableCell>
                        <TableCell>{getLocationName(invoice.location_id)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          ${invoice.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-destructive">
                          {invoice.refund_amount > 0 && `-$${invoice.refund_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          ${(invoice.amount - (invoice.refund_amount || 0))?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-[200px] truncate">
                          {invoice.notes}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleEdit(invoice)}
                              data-testid={`edit-invoice-${invoice.invoice_id}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDelete(invoice.invoice_id)}
                              data-testid={`delete-invoice-${invoice.invoice_id}`}
                            >
                              <Trash size={16} className="text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-16 text-center">
                <Receipt size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No invoices recorded yet</p>
                <p className="text-sm text-muted-foreground mt-1">Click "Add Invoice" to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Vendor Group View */
        <div className="space-y-4">
          {/* Grand Totals Summary */}
          {groupedByVendor.length > 0 && (
            <Card className="border rounded-xl bg-primary/5">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <h3 className="font-heading tracking-wide text-lg">GRAND TOTAL</h3>
                    <Badge variant="secondary" className="text-sm">
                      {groupedByVendor.length} vendors
                    </Badge>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="ml-2 font-semibold tabular-nums">
                        ${grandTotals.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Refunds:</span>
                      <span className="ml-2 font-semibold tabular-nums text-destructive">
                        -${grandTotals.refund.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Net:</span>
                      <span className="ml-2 font-bold tabular-nums text-primary">
                        ${grandTotals.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={expandAll}>
                      Expand All
                    </Button>
                    <Button variant="outline" size="sm" onClick={collapseAll}>
                      Collapse All
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vendor Groups */}
          {groupedByVendor.length > 0 ? (
            groupedByVendor.map(group => (
              <Collapsible 
                key={group.vendor} 
                open={expandedVendors[group.vendor]} 
                onOpenChange={() => toggleVendor(group.vendor)}
              >
                <Card className="border rounded-xl overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedVendors[group.vendor] ? (
                            <CaretDown size={20} className="text-muted-foreground" />
                          ) : (
                            <CaretRight size={20} className="text-muted-foreground" />
                          )}
                          <div>
                            <CardTitle className="font-heading tracking-wide text-lg">
                              {group.vendor}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {group.invoices.length} invoice{group.invoices.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-6 text-sm">
                          <div className="text-right">
                            <p className="text-muted-foreground text-xs">Amount</p>
                            <p className="font-semibold tabular-nums">
                              ${group.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          {group.totalRefund > 0 && (
                            <div className="text-right">
                              <p className="text-muted-foreground text-xs">Refunds</p>
                              <p className="font-semibold tabular-nums text-destructive">
                                -${group.totalRefund.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                          )}
                          <div className="text-right min-w-[100px]">
                            <p className="text-muted-foreground text-xs">Net Total</p>
                            <p className="font-bold tabular-nums text-primary text-lg">
                              ${group.totalNet.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="p-0 border-t">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="font-heading tracking-wide text-xs">DATE</TableHead>
                            <TableHead className="font-heading tracking-wide text-xs">LOCATION</TableHead>
                            <TableHead className="font-heading tracking-wide text-xs text-right">AMOUNT</TableHead>
                            <TableHead className="font-heading tracking-wide text-xs text-right">REFUND</TableHead>
                            <TableHead className="font-heading tracking-wide text-xs text-right">NET</TableHead>
                            <TableHead className="font-heading tracking-wide text-xs">NOTES</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.invoices.map(invoice => (
                            <TableRow key={invoice.invoice_id}>
                              <TableCell className="tabular-nums py-2">
                                {format(new Date(invoice.invoice_date), "MMM d, yyyy")}
                              </TableCell>
                              <TableCell className="py-2">{getLocationName(invoice.location_id)}</TableCell>
                              <TableCell className="text-right tabular-nums py-2">
                                ${invoice.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right tabular-nums text-destructive py-2">
                                {invoice.refund_amount > 0 && `-$${invoice.refund_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                              </TableCell>
                              <TableCell className="text-right tabular-nums font-medium py-2">
                                ${(invoice.amount - (invoice.refund_amount || 0))?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-muted-foreground max-w-[200px] truncate py-2">
                                {invoice.notes}
                              </TableCell>
                              <TableCell className="py-2">
                                <div className="flex gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-7 w-7"
                                    onClick={() => handleEdit(invoice)}
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleDelete(invoice.invoice_id)}
                                  >
                                    <Trash size={14} className="text-destructive" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))
          ) : (
            <Card className="border rounded-xl">
              <CardContent className="py-16 text-center">
                <Receipt size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No invoices recorded yet</p>
                <p className="text-sm text-muted-foreground mt-1">Click "Add Invoice" to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>

    {/* Vendor Management Dialog */}
    <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-wide">
            {editingVendor ? "EDIT VENDOR" : "MANAGE VENDORS"}
          </DialogTitle>
        </DialogHeader>
        {!editingVendor ? (
          <div className="space-y-4">
            <Button size="sm" onClick={() => { setEditingVendor({}); setVendorForm({ name: "", contact_person: "", phone: "", email: "", notes: "" }); }} data-testid="add-vendor-btn">
              <Plus size={16} className="mr-2" /> Add Vendor
            </Button>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {vendors.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-4">No vendors yet. Add your first vendor.</p>
              ) : vendors.map(v => (
                <div key={v.vendor_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`vendor-row-${v.vendor_id}`}>
                  <div>
                    <p className="font-medium text-sm">{v.name}</p>
                    {v.contact_person && <p className="text-xs text-muted-foreground">{v.contact_person}</p>}
                    {(v.phone || v.email) && <p className="text-xs text-muted-foreground">{[v.phone, v.email].filter(Boolean).join(' · ')}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingVendor(v); setVendorForm({ name: v.name, contact_person: v.contact_person || "", phone: v.phone || "", email: v.email || "", notes: v.notes || "" }); }}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteVendor(v.vendor_id)}>
                      <Trash size={14} className="text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Vendor Name *</Label>
              <Input value={vendorForm.name} onChange={(e) => setVendorForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Sysco" data-testid="vendor-form-name" />
            </div>
            <div>
              <Label className="text-xs">Contact Person</Label>
              <Input value={vendorForm.contact_person} onChange={(e) => setVendorForm(p => ({...p, contact_person: e.target.value}))} placeholder="e.g., John Smith" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={vendorForm.phone} onChange={(e) => setVendorForm(p => ({...p, phone: e.target.value}))} placeholder="(555) 123-4567" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={vendorForm.email} onChange={(e) => setVendorForm(p => ({...p, email: e.target.value}))} placeholder="vendor@email.com" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={vendorForm.notes} onChange={(e) => setVendorForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingVendor(null)}>Back</Button>
              <Button onClick={handleSaveVendor} data-testid="save-vendor-btn">
                <Check size={16} className="mr-2" /> {editingVendor?.vendor_id ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
